import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { adminApi } from '../../services/adminApi';
import { CreditCard, Loader2, DollarSign, AlertTriangle, CheckCircle2 } from 'lucide-react';

export default function Payments() {
  const ctx = useOutletContext();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  useEffect(() => { adminApi.getPayments().then(d => { setData(d); setLoading(false); }); }, []);
  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-blue-400" /></div>;

  const filtered = filter ? data.filter(p => p.payment_status === filter) : data;
  const totalRevenue = data.reduce((a, p) => a + p.amount_paid, 0);
  const totalOutstanding = data.reduce((a, p) => a + p.balance_due, 0);
  const paidCount = data.filter(p => p.deposit_status === 'Paid').length;

  return (
    <div data-testid="admin-payments">
      <h1 className="text-2xl font-bold mb-1" style={{ fontFamily: 'Chivo, sans-serif' }}>Payments Dashboard</h1>
      <p className={`text-sm ${ctx.textSecondary} mb-6`}>{data.length} payment records</p>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <div className={`${ctx.cardBg} rounded-xl border ${ctx.borderColor} p-4`}>
          <DollarSign className="w-5 h-5 text-emerald-400 mb-2" />
          <p className="text-xl font-bold">${totalRevenue}</p>
          <p className={`text-xs ${ctx.textSecondary}`}>Total Revenue</p>
        </div>
        <div className={`${ctx.cardBg} rounded-xl border ${ctx.borderColor} p-4`}>
          <AlertTriangle className="w-5 h-5 text-amber-400 mb-2" />
          <p className="text-xl font-bold">${totalOutstanding}</p>
          <p className={`text-xs ${ctx.textSecondary}`}>Outstanding Balances</p>
        </div>
        <div className={`${ctx.cardBg} rounded-xl border ${ctx.borderColor} p-4`}>
          <CheckCircle2 className="w-5 h-5 text-blue-400 mb-2" />
          <p className="text-xl font-bold">{paidCount}/{data.length}</p>
          <p className={`text-xs ${ctx.textSecondary}`}>Deposits Collected</p>
        </div>
        <div className={`${ctx.cardBg} rounded-xl border ${ctx.borderColor} p-4`}>
          <CreditCard className="w-5 h-5 text-purple-400 mb-2" />
          <p className="text-xl font-bold">Stripe</p>
          <p className={`text-xs ${ctx.textSecondary}`}>Payment Provider</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {['', 'Complete', 'Partial', 'Pending'].map(s => (
          <button key={s} onClick={() => setFilter(s)} className={`px-3 py-1.5 rounded-full text-xs font-medium ${filter === s ? 'bg-blue-600 text-white' : `${ctx.hoverBg} ${ctx.textSecondary}`}`}>{s || 'All'}</button>
        ))}
      </div>

      <div className={`${ctx.cardBg} rounded-xl border ${ctx.borderColor} overflow-x-auto`}>
        <table className="w-full text-sm">
          <thead><tr className={`border-b ${ctx.borderColor}`}>
            {['Reservation', 'Student', 'Deposit', 'Amount Paid', 'Balance Due', 'Method', 'Status'].map(h => (
              <th key={h} className={`text-left px-4 py-3 text-xs font-medium ${ctx.textSecondary} uppercase tracking-wider`}>{h}</th>
            ))}
          </tr></thead>
          <tbody>{filtered.map(p => (
            <tr key={p._id} className={`border-b ${ctx.borderColor} ${ctx.hoverBg}`}>
              <td className="px-4 py-3 font-mono text-xs">{p.reservation_number}</td>
              <td className="px-4 py-3 text-sm">{p.student_name}</td>
              <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full ${p.deposit_status === 'Paid' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-amber-500/10 text-amber-400'}`}>{p.deposit_status}</span></td>
              <td className="px-4 py-3 font-medium">${p.amount_paid}</td>
              <td className="px-4 py-3 font-medium text-amber-400">${p.balance_due}</td>
              <td className="px-4 py-3 text-xs">{p.payment_method}</td>
              <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full ${p.payment_status === 'Complete' ? 'bg-emerald-500/10 text-emerald-400' : p.payment_status === 'Partial' ? 'bg-blue-500/10 text-blue-400' : 'bg-gray-500/10 text-gray-400'}`}>{p.payment_status}</span></td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  );
}
